// Mirror of EuropassPdfLanguageSkillsSection.jsx: bullet title, native languages
// line, "other languages" label, a proficiency table (grey rules, white header,
// black text) and the CEFR legend.

import { AlignmentType, BorderStyle, Table, TableCell, TableRow, WidthType } from "docx";
import { getNativeLanguageCodes } from "../../../../utils/getNativeLanguageCodes";
import { bulletSectionTitle, NO_CELL_BORDERS, NO_TABLE_BORDERS, para, run } from "../../shared/wordHelpers";
import { EUROPASS_RULE, FONT_VERDANA, TEXT_BLACK } from "../../shared/wordTheme";
import { EU_INDENT, EU_TABLE_INDENT, EU_TABLE_WIDTH, euPara } from "../euLayout";

const FRACTIONS = [ 0.30, 0.14, 0.14, 0.14, 0.14, 0.14 ];
const COLS = FRACTIONS.map( f => Math.round( EU_TABLE_WIDTH * f ) );

function spanWidth( span ) {
    return COLS.slice( 0, span ).reduce( ( a, b ) => a + b, 0 );
}

function tableCell( paragraph, { span = 1, rule } = {} ) {
    return new TableCell( {
        columnSpan: span,
        width: { size: spanWidth( span ), type: WidthType.DXA },
        borders: rule
            ? { ...NO_CELL_BORDERS, bottom: { style: BorderStyle.SINGLE, size: 6, color: EUROPASS_RULE } }
            : NO_CELL_BORDERS,
        children: [ paragraph ],
    } );
}

function centered( text, { pt = 8, bold } = {} ) {
    return para( run( text, { pt, bold, color: TEXT_BLACK, font: FONT_VERDANA } ), { alignment: AlignmentType.CENTER, spacing: { after: 0 } } );
}

export function buildEuropassLanguages( { data, props, translations } ) {
    const { languageSkills } = data;
    const { documentLanguage } = props;
    const t = translations?.pdf ?? {};
    const nativeCodes = getNativeLanguageCodes( languageSkills );

    const nativeList = languageSkills
        .filter( skill => skill.language )
        .filter( skill => nativeCodes.includes( skill.language.code ) )
        .sort( ( a, b ) => a.order - b.order )
        .map( skill => skill.language?.translations?.[ documentLanguage ] ?? skill.language?.code ?? "" )
        .join( ", " );

    const elements = bulletSectionTitle( t.languageSkillsTitle, { font: FONT_VERDANA, indent: EU_INDENT } );

    elements.push( euPara( [
        run( `${ t.nativeLanguagesLabel } `, { pt: 8, color: TEXT_BLACK, font: FONT_VERDANA } ),
        run( nativeList, { pt: 8, bold: true, allCaps: true, color: TEXT_BLACK, font: FONT_VERDANA } ),
    ] ) );
    elements.push( euPara( run( t.otherLanguagesLabel, { pt: 8, color: TEXT_BLACK, font: FONT_VERDANA } ), { spacing: { before: 80, after: 0 } } ) );

    const groupHeader = new TableRow( {
        children: [
            tableCell( para( [], { spacing: { after: 0 } } ) ),
            tableCell( centered( t.comprehensionHeader ), { span: 2, rule: true } ),
            tableCell( centered( t.oralExpressionHeader ), { span: 2, rule: true } ),
            tableCell( centered( t.writingExpressionHeader ), { span: 1, rule: true } ),
        ],
    } );
    const subHeader = new TableRow( {
        children: [
            tableCell( para( [], { spacing: { after: 0 } } ), { rule: true } ),
            tableCell( centered( t.listeningComprehensionHeader ), { rule: true } ),
            tableCell( centered( t.readingComprehensionHeader ), { rule: true } ),
            tableCell( centered( t.continuousOralExpressionHeader ), { rule: true } ),
            tableCell( centered( t.oralInteractionHeader ), { rule: true } ),
            tableCell( para( [], { spacing: { after: 0 } } ), { rule: true } ),
        ],
    } );

    const dataRows = languageSkills
        .filter( skill => skill.language )
        .filter( skill => !nativeCodes.includes( skill.language.code ) )
        .sort( ( a, b ) => a.order - b.order )
        .map( skill => {
            const level = key => skill[ key ]?.translations?.[ documentLanguage ] ?? "";
            return new TableRow( {
                children: [
                    tableCell( para( run( skill.language?.translations?.[ documentLanguage ] ?? skill.language?.code ?? "", { pt: 10, bold: true, color: TEXT_BLACK, font: FONT_VERDANA } ), { spacing: { after: 0 } } ), { rule: true } ),
                    tableCell( centered( level( "listeningComprehensionLevel" ), { pt: 10 } ), { rule: true } ),
                    tableCell( centered( level( "readingComprehensionLevel" ), { pt: 10 } ), { rule: true } ),
                    tableCell( centered( level( "continuousOralExpressionLevel" ), { pt: 10 } ), { rule: true } ),
                    tableCell( centered( level( "oralInteractionLevel" ), { pt: 10 } ), { rule: true } ),
                    tableCell( centered( level( "writingExpressionLevel" ), { pt: 10 } ), { rule: true } ),
                ],
            } );
        } );

    elements.push( new Table( {
        width: { size: EU_TABLE_WIDTH, type: WidthType.DXA },
        columnWidths: COLS,
        indent: { size: EU_TABLE_INDENT, type: WidthType.DXA },
        borders: NO_TABLE_BORDERS,
        rows: [ groupHeader, subHeader, ...dataRows ],
    } ) );

    elements.push( euPara( run(
        "Niveaux : A1 et A2 : utilisateur de base; B1 et B2 : utilisateur indépendant; C1 et C2 : utilisateur expérimenté.",
        { pt: 6, color: TEXT_BLACK, font: FONT_VERDANA }
    ) ) );

    return elements;
}
